import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { IExpenses } from "./ExpensesViewController";
import {
  EXPENSES_FIELD_INITIAL_VALUES,
  expensesFieldValidationSchema,
  fetchExpenseFieldById,
  getExpenseHeadDdl,
  handleSubmitExpenses,
  IExpensesForm,
} from "./ExpensesFormController";
import { getLocalDate, hasAnyModification } from "../../utils/AppFunctions";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import { Formik, Form } from "formik";
import {
  ICFormikDatePicker,
  ICFormikDropDown,
  ICFormikTextInput,
} from "../../core-component/formik";
import { DATE_FORMAT, IMAGE_TYPE } from "../../utils/AppEnumerations";
import { IOption } from "../../data/AppInterface";
import { ICFileUpload } from "../../core-component/form";
import { PAYMENT_METHOD_STATUS_OPTION } from "../../utils/AppConstants";
import { fetchUploadImageService } from "../global/image-service/ImageServices";
import ICFormikTextArea from "../../core-component/formik/ICFormikTextIArea";
interface ITaxComponentFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  expensesToEdit?: IExpenses;
}

const ExpensesForm: React.FC<ITaxComponentFormProps> = ({
  handleClose,
  readOnly,
  expensesToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [expensesFormInitialValues, setExpensesFormInitialValues] =
    useState<IExpensesForm>();
  const [expenseHeads, setExpenseHeads] = useState<IOption<string>[]>([]);
  const [typeImage, setTypeImage] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  useEffect(() => {
    if (expensesToEdit) {
      getExpensesFieldById(expensesToEdit.id);
    } else {
      setExpensesFormInitialValues(EXPENSES_FIELD_INITIAL_VALUES);
      fetchExpenseHead();
    }
  }, []);

  const getExpensesFieldById = async (id: string) => {
    setLoading(true);
    const expenseField = await fetchExpenseFieldById(id, handleClose);
    setExpensesFormInitialValues(expenseField);
    fetchExpenseHead(expenseField.expense_head);
    setLoading(false);
  };

  const checkFormModification = (values: IExpensesForm) => {
    if (
      expensesFormInitialValues &&
      hasAnyModification(values, expensesFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const fetchExpenseHead = async (expenseHeadValue?: string) => {
    setLoading(true);
    await getExpenseHeadDdl(setExpenseHeads, expenseHeadValue);
    setLoading(false);
  };

  useEffect(() => {
    setTypeImage(
      expensesFormInitialValues?.receipt_media_url
        ? [expensesFormInitialValues.receipt_media_url]
        : []
    );
  }, [expensesFormInitialValues]);

  const handleSubmit = async (values: IExpensesForm) => {
    setSubmitLoading(true);

    const updatedTypeMedia = typeImage.map((url) => ({
      media_url: url,
    }));

    if (selectedFiles.length > 0) {
      const formData = new FormData();
      formData.append("ImageFile", selectedFiles[0]);
      formData.append("type", IMAGE_TYPE.Expense);
      if (values.id) {
        formData.append("typeId", values.id.toString());
      }

      const mediaUrl = await fetchUploadImageService(formData);
      if (!mediaUrl) {
        return null;
      }

      updatedTypeMedia.push({ media_url: mediaUrl });
    }

    await handleSubmitExpenses(
      {
        ...values,
        receipt_media_url: updatedTypeMedia[0]?.media_url,
        amount: Number(values.amount),
        expense_date: values.expense_date
          ? getLocalDate(values.expense_date).toISOString().split("T")[0]
          : undefined,
        transaction_reference: values.transaction_reference
          ? values.transaction_reference
          : undefined,
      },
      setSubmitLoading,
      handleClose,
      expensesToEdit?.id
    );
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {expensesFormInitialValues && (
            <Formik
              initialValues={expensesFormInitialValues}
              validationSchema={expensesFieldValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/3"
                    onClose={() => checkFormModification(values)}
                    title={t("expense")}
                    footer={{
                      created_date: expensesFormInitialValues.created_date,
                      modified_date: expensesFormInitialValues.modified_date,
                      created_by: expensesFormInitialValues.created_by,
                      modified_by: expensesFormInitialValues.modified_by,
                    }}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-scroll py-4 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"title"}
                          placeholder={t("title")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikDropDown
                          {...formikPorps}
                          name={"expense_head"}
                          placeholder={t("expense_head")}
                          options={expenseHeads}
                          required
                          searchable
                          disabled={readOnly}
                        />
                        <ICFormikDatePicker
                          {...formikPorps}
                          fieldName={"expense_date"}
                          placeholder={t("expense_date")}
                          dateProps={{
                            displayFormat: DATE_FORMAT["DD-Mon-YYYY"],
                            max: getLocalDate(),
                          }}
                          required
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikPorps}
                          type="number"
                          fieldName={"amount"}
                          placeholder={t("amount")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikDropDown
                          {...formikPorps}
                          name={"payment_method"}
                          placeholder={t("payment_method")}
                          options={PAYMENT_METHOD_STATUS_OPTION}
                          required
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"transaction_reference"}
                          placeholder={t("transaction_reference_no")}
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikTextArea
                          {...formikPorps}
                          name={"description"}
                          label={t("description")}
                          disabled={readOnly}
                        />
                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_image")}
                            maxFiles={1}
                            maxFileSizeMB={10}
                            acceptedFileTypes={["jpg", "png", "jpeg"]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={typeImage}
                            setData={setTypeImage}
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
export default ExpensesForm;
